import React, { useEffect, useState } from 'react';
import { Snackbar, Box, Typography, Avatar, IconButton, Paper, styled } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { notificationImageMap, defaultNotificationImage } from '../drawer/NotificationSidebar';

// interface NotificationData {
//   notification_category_code: string;
//   message: string;
//   event_type_name: 'alert' | 'notification';
// }

// interface QueuedNotification extends NotificationData {
//   id: number;
// }

import eventEmitter from '../utils/eventEmitter';

const categories = [
  { codes: ['decommission_device', 'recommission_device', 'modify_device_location', 'delete_device'], title: 'Asset Management' },
  { codes: ['add_region_data', 'delete_region_data', 'modify_region_level', 'modify_region_name'], title: 'Region Management' },
  { codes: ['accepted_risk', 'risk_approved', 'risk_rejected', 'risk_submitted', 'risk_request', 'risk_acceptance', 'modify_accepted_risk', 'whitelist_request'], title: 'Accepted Risk' },
  { codes: ['api_connection_new_device', 'file_upload_new_device', 'ssh_connection_new_device', 'rescan'], title: 'New File Upload' },
  { codes: ['role_delete', 'role_modification'], title: 'Role Management' },
  { codes: ['activate_user', 'deactivate_user', 'invite_user', 'modify_user_role'], title: 'User Management' },
  { codes: ['master_file_upload'], title: 'My Scans' },
];

const titleMap = new Map<string, string>();
categories.forEach((category) => {
  category.codes.forEach((code) => {
    titleMap.set(code, category.title);
  });
});

const StyledSnackbar = styled(Snackbar)(({ theme }) => ({
  '& .MuiSnackbarContent-root': {
    backgroundColor: 'white',
    color: 'black',
    minWidth: '400px',
    borderRadius: '8px',
    boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
    padding: '0px'
  }
}));

const NotificationContent = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  padding: '12px 16px',
  gap: '12px'
}));

const NotificationManager: React.FC = () => {
  const [queue, setQueue] = useState<any>([]);
  const [currentNotification, setCurrentNotification] = useState<any>(null);
  const [isOpen, setIsOpen] = useState(false);

 
  const addToQueue = (notification: any) => {
    setQueue((prev:any) => [...prev, { ...notification, id: Date.now() }]);
  };

  useEffect(() => {
  const handler = (event: Event) => {
    const customEvent = event as CustomEvent;
    addToQueue(customEvent.detail);
  };
  window.addEventListener('show-notification', handler);
  return () => window.removeEventListener('show-notification', handler);
}, []);

  
  useEffect(() => {
    if (!currentNotification && queue.length > 0) {
      const nextNotification = queue[0];
      setCurrentNotification(nextNotification);
      setQueue((prev:any) => prev.slice(1));
      setIsOpen(true);
    }
  }, [queue, currentNotification]);

 
  const handleClose = (event?: React.SyntheticEvent | Event, reason?: string) => {
    if (reason === 'clickaway') return;
    setIsOpen(false);
    setTimeout(() => {
      setCurrentNotification(null);
    }, 300); 
  };

 
  useEffect(() => {
    if (isOpen) {
      const timer = setTimeout(() => {
        handleClose();
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [isOpen]);

  const handleNotificationRedirection = (e?: React.MouseEvent) => {
    if (e) e.stopPropagation();
    handleClose();
    eventEmitter.emit("closeAllDrawers");
    window.dispatchEvent(
      new CustomEvent("open-notification-sidebar", {
        detail: {
          tab: "notifications",
          filter: "all",
          path: "/on-premises/acceptedrisk",
        },
      })
    );
  };

  if (!currentNotification) return null;

  const title = titleMap.get(currentNotification.notification_category_code) || 'Notification';
  const color = currentNotification.event_type_name?.toLowerCase() === 'alert' ? '#dc3545' : '#007bff';

  const messageParts = Array.isArray(currentNotification.message)
    ? currentNotification.message
    : [{ mesg: currentNotification.message }];
  const isMultipleParameters = messageParts.some((item: any) =>
    `${item?.mesg || ""} ${item?.fullmesg || ""}`
      .replace(/<[^>]*>/g, "")
      .toLowerCase()
      .includes("multiple parameters")
  );
  const displayMessage = isMultipleParameters
    ? [
        ...messageParts.map((item: any) => ({
          ...item,
          redirection: false,
        })),
        {
          mesg: " Click here to view notifications.",
          redirection: true,
        },
      ]
    : messageParts;

  return (
    <StyledSnackbar
      open={isOpen}
      onClose={handleClose}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      autoHideDuration={6000}
    >
      <Paper
        elevation={3}
        sx={{
          minWidth: 340,
          maxWidth: 400,
          borderRadius: 2,
          p: 0,
          boxShadow: '0 2px 12px rgba(0,0,0,0.08)',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            px: 2,
            pt: 2,
            pb: 1,
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Avatar
              src={notificationImageMap[currentNotification.notification_category_code] || defaultNotificationImage}
              variant="rounded"
              sx={{ width: 28, height: 28 }}
            />
            <Typography variant="subtitle2" sx={{ color, fontWeight: 700, fontSize: 16 }}>
              {title}
            </Typography>
          </Box>
          <IconButton
            size="small"
            aria-label="close"
            color="inherit"
            onClick={handleClose}
            sx={{ ml: 1 }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>
        <Box
          sx={{
            borderRadius: '0 0 8px 8px',
            px: 2,
            pb: 2,
            pt: 0.5,
          }}
        >
          {Array.isArray(currentNotification.message) || isMultipleParameters ? (
            <Typography variant="body2">
              {displayMessage.map((item: any, idx: number) => {
                const isRedirect = Boolean(item.redirection);
                return (
                  <Box
                    component="span"
                    key={idx}
                    onClick={isRedirect ? handleNotificationRedirection : undefined}
                    sx={{
                      cursor: isRedirect ? "pointer" : "inherit",
                      color: isRedirect ? "#3F65ED" : "inherit",
                      fontWeight: isRedirect || /<b>.*<\/b>/.test(item.mesg) ? 600 : "inherit",
                      textDecoration: isRedirect ? "underline" : "none",
                      display: "inline",
                      "&:hover": isRedirect ? { color: "#0F02C3" } : {},
                    }}
                    dangerouslySetInnerHTML={{ __html: item.mesg }}
                  />
                );
              })}
            </Typography>
          ) : (
            <Typography
              variant="body2"
              dangerouslySetInnerHTML={{ __html: currentNotification.message }}
            />
          )}
        </Box>
      </Paper>
    </StyledSnackbar>
  );
};

export const showNotification = (notification: any) => {
  window.dispatchEvent(new CustomEvent('show-notification', { detail: notification }));
};

export default NotificationManager; 